/**
 * /dashboard entry. Order matters (brief §0.6): read the snapshots (inlined by the Worker: who you
 * are + sidebar prefs, and the meeting), hydrate the stores, THEN render, so the first frame has real
 * data (no spinners, no skeletons); the live socket opens after paint (bootstrap.ts).
 */
import "./lib/polyfills";   // first: pdf.js needs them (lib/polyfills.ts)
import "./styles/themes.css";
import "./styles/tokens.css";
import "./styles/components.css";
import "./styles/app.css";
import "./styles/mobile.css";
import "./plans/plans.css";   // Plan limits in the app (plans/LimitCards.tsx)
import "./styles/reduced-motion.css";
import { useEffect, useState } from "react";
import { useBusy } from "./hooks/useBusy";
import { createRoot } from "react-dom/client";
import { bootMeeting } from "./realtime/bootstrap";
import type { LiveChannel } from "./realtime/liveClient";
import { Shell, type Me } from "./app/Shell";
import { syncAds } from "../site/js/ad-rules.js";
import { LeaderboardAd } from "./ads/AdSlot";
import { interceptLinks, loadAsPage, navigate, pageKeyOf, parse, setPreparer, useRoute } from "./app/router";
import { MeetingPage } from "./pages/MeetingPage";
import { setViewer } from "./app/viewer";
import { publicMeetingPath } from "./app/meetingLinks";
import { PlayerHost } from "./components/meeting/VideoStage";
import { MeetingsPage } from "./pages/MeetingsPage";
import { followingStore } from "./stores/followingStore";
import { connectionStore } from "./stores/connectionStore";
import { startAt } from "./components/meeting/vimeoClock";
import { parseT } from "./lib/format";
import { transcriptSearch } from "./components/meeting/transcriptSearch";
import { navStore } from "./nav/navStore";
import { emptyPrefs, type NavPrefs, type UserGroup } from "./nav/navConfig";
import { initViewerFeatures, useFeaturesVersion } from "./realtime/entitlements";
import { Toaster } from "./components/shared/Toaster";
import { notesStore } from "./stores/notesStore";
import { followsStore } from "./stores/followsStore";
import type { Follow, FollowingSettings, FollowingSnapshot, Note } from "./types";
// Gap screens (build step 13): onboarding, settings, access, notifications, states
import { Onboarding } from "./onboarding/Onboarding";
import { Settings } from "./settings/Settings";
// ---------- Approvals: Chicago Politics staff (/dashboard/admin) ----------
import { Approvals } from "./admin/Approvals";
import type { AdminOrgSnapshot, AdminOrgsSnapshot, AnalysisStatus, ApprovalsSnapshot } from "./admin/types";
import { AdminOrgs, AdminOrg } from "./admin/Organizations";
import { AdminAnalysis } from "./admin/Analysis";
import { AdminAlerts } from "./admin/Alerts";
import { AdminSigners } from "./admin/Signers";
import { AdminSignerMap } from "./admin/SignerMap";
import { AdminArticle, AdminArticles } from "./admin/Articles";
import { AdminTraffic } from "./admin/Traffic";
import { AdminAds } from "./admin/Ads";
import { NOT_AVAILABLE, NotAvailable } from "./components/shared/States";
import { notificationsStore } from "./notifications/notifications";
import { GROUPS, TABS, type TabId } from "./nav/navConfig"; // `can` is imported below (People)
import type { AccessSnapshot, AccountSnapshot, NotificationsSnapshot } from "./account/types";
// People section (screenshots/18–24, 29–30)
import { PeoplePage } from "./people/PeoplePage";
import { ProfilePage, type ProfileTab } from "./people/ProfilePage";
import { peopleStore } from "./people/peopleStore";
import type { CompareData, PeopleSnapshot, PersonProfile } from "./people/model";
import { Compare } from "./people/Compare";
import { can } from "./nav/navConfig";
// ---------- Legislation (build steps 11–12) ----------
import { LegislationPage, bootLegislation, legislationDetailStore } from "./legislation/LegislationPage";
import { listStore } from "./legislation/listStore";
import { armTeamLive } from "./realtime/teamLive";
// ---------- Organizations (screenshots/56–66) ----------
import { OrganizationsPage, bootOrganizations } from "./organizations/OrganizationsPage";
// ---------- Overview (screenshots/67–74) ----------
import { OverviewPage } from "./overview/OverviewPage";
import { overviewStore } from "./overview/overviewStore";
import { bootOverviewLive } from "./overview/live";
import { ThemesPage } from "./overview/ThemesPage";
import type { OverviewSnapshot } from "./overview/types";
// ---------- Analytics: custom dashboards (screenshots/82–86, 91) ----------
import { AnalyticsHome } from "./analytics/AnalyticsHome";
import { DashboardPage } from "./analytics/DashboardPage";
import { PublicDashboard, routeDatasetsThroughLink } from "./analytics/PublicDashboard";
import { analyticsStore } from "./analytics/analyticsStore";
import { fetchDashboard, openDashboard, openLink } from "./analytics/openDashboard";
import type { AnalyticsSnapshot, DashboardTitle, PublicDashboardSnapshot } from "./analytics/types";
// ---------- Sign up, sign in, invites (screenshots/93–97) ----------
import { AcceptInvite, SignedOut } from "./auth/AuthRoutes";
import { showOnboarding } from "./onboarding/onboardingFlow";
import { TeamAccessPage, TeamSettingsPage, type TeamSettingsSnapshot } from "./settings/TeamSettingsPage";
import { SystemPage, type SystemKind } from "./system/SystemPage";
import { passView } from "./auth/civicPass";
import { CommandPalette } from "./search/CommandPalette";
// ---------- Search results (screenshots/114) ----------
import { SearchPage } from "./search/SearchPage";
import { initAppearance } from "./theme/runtime";
// ---------- Casework (offices) and Campaigns (the Advocacy add-on), design section 11 ----------
import { CaseworkPage } from "./casework/CaseworkPage";
import { CampaignsPage } from "./campaigns/CampaignsPage";

let channel: LiveChannel | null = null;
let bootedMeeting: string | null = null;
let bootError: number | null = null;   // for bootedMeeting

const inlined = <T,>(id: string): T | null => {
  const el = document.getElementById(id);
  try { return el?.textContent ? (JSON.parse(el.textContent) as T) : null; } catch { return null; }
};

async function boot(meetingId: string, search = location.search, live = true) {
  channel?.stop();
  channel = null;
  bootedMeeting = meetingId;
  try {
    channel = await bootMeeting(meetingId, { live });
    notesStore.getState().hydrate(meetingId, inlined<Note[]>(`snapshot:notes:${meetingId}`) ?? []);
    bootError = null;
    const params = new URLSearchParams(search);
    const t = parseT(params.get("t"));
    if (t) startAt(t.start, t.end);
    // ?q=: the transcript opens searching for those words (a "Search what was said" result), from ?t= when given.
    const q = params.get("q")?.trim();
    if (q) transcriptSearch.open(q, t?.start ?? null);
  } catch (e) {
    bootError = Number(String(e).match(/snapshot (\d+)/)?.[1]) || 500;
  }
}

/** Not signed in, or not admitted yet (plain panel text: these states aren't in the design). */
function AccessState({ me }: { me: Me }) {
  // A team-account request (onboarding step 1) is pending too.
  const [access, setAccess] = useState(me.onboarding?.teamRequest?.status === "requested" && !me.access ? "requested" : me.access ?? "none");
  const next = encodeURIComponent(location.pathname);
  const { busy, run } = useBusy();
  if (!me.signedIn) return <p className="empty-panel">Sign in to open the dashboard. <a className="link" href={`/signin?next=${next}`}>Sign in</a></p>;
  if (access === "requested") return <p className="empty-panel">Your request for access is pending. We'll email you when it's approved.</p>;
  if (access === "denied") return <p className="empty-panel">Your account doesn't have dashboard access.</p>;
  return (
    <p className="empty-panel">
      Your account doesn't have dashboard access yet.{" "}
      <button className="link" disabled={busy} aria-busy={busy || undefined} onClick={() => void run(async () => {
        const r = await fetch("/api/dashboard/request-access", { method: "POST", credentials: "include" }).catch(() => null);
        if (r?.ok) setAccess("requested");
      })}>Request access</button>
    </p>
  );
}

function MeetingError() {
  return (
    <p className="empty-panel">
      {bootError === 404 ? "This meeting hasn't been processed yet." : "Something went wrong loading this meeting."}
    </p>
  );
}

/** The public meeting page (/meeting/<id>[/<tab>]): the dashboard's meeting page for anyone, on the canvas alone (no sidebar). */
function PublicMeeting({ me }: { me: Me }) {
  const route = useRoute();
  const id = route.meetingId!;
  return (
    <div className="app app--public">
      {/* .main > .panel: the dashboard's scroll container on wide screens (app.css, app/scroller.ts). */}
      <div className="main">
        <main className="panel public-panel">
          {bootError !== null ? <MeetingError /> : (
            <MeetingPage teamAccount={!!me.orgId} tab={route.tab} ads={!me.orgId && !me.staff}
              onTab={(t) => navigate(`/meeting/${encodeURIComponent(id)}${t === "overview" ? "" : `/${t}`}`, { replace: true })} />
          )}
          {/* The data pages' leaderboard ad (ads/AdSlot.tsx), after the meeting; not for team accounts or staff. */}
          {bootError === null && !me.orgId && !me.staff ? <LeaderboardAd /> : null}
        </main>
      </div>
    </div>
  );
}

function App({ me }: { me: Me }) {
  const route = useRoute();
  useFeaturesVersion();   // staff changed this org's features (OrgRoom entitlements.changed): re-render the nav and gated views
  const [teamViews, setTeamViews] = useState(me.teamViews ?? []);
  useEffect(() => {
    // A team view was published (New view dialog): refresh the TEAM VIEWS list.
    const reload = () => me.orgId && fetch(`/api/orgs/${encodeURIComponent(me.orgId)}/views`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setTeamViews(d.views ?? [])).catch(() => {});
    addEventListener("dashboard:teamviews", reload);
    return () => removeEventListener("dashboard:teamviews", reload);
  }, [me.orgId]);

  // Sign up / sign in (93–97): every signed-out /dashboard path; invites are accepted once signed in.
  if (!me.signedIn) return <SignedOut path={route.path} />;
  if (route.section === "invite") return <AcceptInvite path={route.path} />;
  if (route.section === "signin" || route.section === "signup") { location.replace("/dashboard"); return null; }
  // Onboarding (screenshots/48–50): signed-in users who haven't finished it, and Settings' "Change" (/dashboard/welcome).
  const ob = me.onboarding;
  if (me.signedIn && ob && showOnboarding(me, ob, route.section)) {
    return <><Onboarding status={ob} group={me.group ?? null} admitted={!!me.group} path={route.path} orgName={me.orgName ?? null} /><Toaster /></>;
  }
  if (!me.group) {
    return <div className="app app--bare"><main className="panel"><AccessState me={me} /></main></div>;
  }
  const shellMe = { ...me, group: me.group as UserGroup, teamViews };
  // Settings and Access paint from their inlined snapshots; opened from the sidebar, load them as a fresh page.
  const pageSnapshot = <T,>(id: string): T | null => (route.path === bootPath ? inlined<T>(id) : null);
  // These paint only from the snapshots inlined for their path (navigation loads them first; see setPreparer below).
  if ((route.section === "settings" || (route.section === "admin" && me.staff)) && route.path !== bootPath) return loadAsPage();
  // Approvals (staff only; the Worker enforces it too): paints from snapshot:admin. Anyone else: 404.
  if (route.section === "admin" && /^\/dashboard\/admin\/orgs\/[^/]+\/?$/.test(route.path)) {
    const org = me.staff ? pageSnapshot<AdminOrgSnapshot>("snapshot:admin-org") : null;
    if (!org) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminOrg initial={org} /><Toaster /></Shell>;
  }
  if (route.section === "admin" && /^\/dashboard\/admin\/orgs\/?$/.test(route.path)) {
    const orgs = me.staff ? pageSnapshot<AdminOrgsSnapshot>("snapshot:admin-orgs") : null;
    if (!orgs) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminOrgs initial={orgs} /><Toaster /></Shell>;
  }
  // Articles (On My Block, staff only): the page fetches its own data (block-admin.js).
  const articlePath = route.section === "admin" ? route.path.match(/^\/dashboard\/admin\/articles(?:\/([\w-]+))?\/?$/) : null;
  if (articlePath) {
    if (!me.staff) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}>{articlePath[1] ? <AdminArticle id={articlePath[1]} /> : <AdminArticles />}<Toaster /></Shell>;
  }
  // Alerts (ward email alerts, staff only): the page fetches its own data (ward-alerts.js adminAlerts).
  if (route.section === "admin" && /^\/dashboard\/admin\/alerts\/?$/.test(route.path)) {
    if (!me.staff) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminAlerts /><Toaster /></Shell>;
  }
  // Petition signers (staff only; the Worker enforces it on every API call): the page fetches its own data (petition-signers.js).
  if (route.section === "admin" && /^\/dashboard\/admin\/signers\/map\/?$/.test(route.path)) {
    if (!me.staff) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminSignerMap /><Toaster /></Shell>;
  }
  if (route.section === "admin" && /^\/dashboard\/admin\/signers\/?$/.test(route.path)) {
    if (!me.staff) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminSigners /><Toaster /></Shell>;
  }
  // Traffic (GA4 + Search Console across the network, staff only): the page fetches its own data (traffic.js).
  if (route.section === "admin" && /^\/dashboard\/admin\/traffic\/?$/.test(route.path)) {
    if (!me.staff) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminTraffic /><Toaster /></Shell>;
  }
  // Ads (the ad marketplace's queue and campaigns, staff only): the page fetches its own data (admarket.js).
  if (route.section === "admin" && /^\/dashboard\/admin\/ads\/?$/.test(route.path)) {
    if (!me.staff) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminAds /><Toaster /></Shell>;
  }
  if (route.section === "admin" && /^\/dashboard\/admin\/analysis\/?$/.test(route.path)) {
    const analysis = me.staff ? pageSnapshot<AnalysisStatus>("snapshot:admin-analysis") : null;
    if (!analysis) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><AdminAnalysis initial={analysis} /><Toaster /></Shell>;
  }
  if (route.section === "admin") {
    const admin = me.staff ? pageSnapshot<ApprovalsSnapshot>("snapshot:admin") : null;
    if (!admin) return <System kind="notFound" me={shellMe} />;
    return <Shell me={shellMe}><Approvals initial={admin} /><Toaster /></Shell>;
  }
  if (route.section === "settings") {
    // Team accounts: Settings is Team settings (screenshots/119); "Your account" is /dashboard/settings/you.
    const team = pageSnapshot<TeamSettingsSnapshot>("snapshot:team-settings");
    // Members & roles (the Access screen, 52) is a Team settings page: owners and admins (feature `access`; the Worker enforces it).
    if (/^\/dashboard\/settings\/access\/?$/.test(route.path)) {
      const access = team && can(shellMe.group, me.role ?? null, "access") ? pageSnapshot<AccessSnapshot>("snapshot:access") : null;
      if (!access) return <Shell me={shellMe}><NotAvailable title="Members & roles" body={team ? "Only team owners and admins manage who's on the team." : "Team accounts only."} action="Settings" href="/dashboard/settings" /></Shell>;
      return <Shell me={shellMe}><TeamAccessPage team={team!} initial={access} /><Toaster /></Shell>;
    }
    if (team) return <Shell me={shellMe}><TeamSettingsPage initial={team} /><Toaster /></Shell>;
    const account = pageSnapshot<AccountSnapshot>("snapshot:account");
    return <Shell me={shellMe}>{account ? <Settings account={account} canManageTeam={can(shellMe.group, me.role ?? null, "teamFollows") && (me.role === "owner" || me.role === "admin")} /> : null}<Toaster /></Shell>;
  }
  // A tab your group (or role) doesn't have, opened by URL (screenshots/54 state 9).
  const gated = GROUPS[shellMe.group].unavailableReason[route.section as TabId];
  // A tab the group has but staff turned off for this org (snapshot:me.features): the plain Not available state.
  if ((route.section === "analytics" || route.section === "organizations" || route.section === "casework" || route.section === "campaigns") && gated === undefined && !can(shellMe.group, me.role ?? null, route.section)) {
    return <Shell me={shellMe}><NotAvailable title={TABS[route.section].label} /></Shell>;
  }
  if (gated !== undefined) {
    const copy = NOT_AVAILABLE[route.section];
    const label = TABS[route.section as TabId]?.label ?? route.section;
    return <Shell me={shellMe}><NotAvailable title={copy?.title ?? label} body={copy?.body ?? (gated || undefined)} action={copy?.action} href={copy?.href} /></Shell>;
  }
  if (route.meetingId) {
    // Navigation boots the meeting before it renders; a meeting that somehow wasn't booted loads as a page.
    if (route.meetingId !== bootedMeeting) return loadAsPage();
    return (
      <Shell me={shellMe}>
        {bootError !== null ? <MeetingError /> : (
          <MeetingPage
            teamAccount={!!me.orgId}
            tab={route.tab}
            onTab={(t) => navigate(`/dashboard/meetings/${encodeURIComponent(route.meetingId!)}${t === "overview" ? "" : `/${t}`}`, { replace: true })}
          />
        )}
      </Shell>
    );
  }
  if (route.section === "people") {
    const group = me.group as UserGroup;
    if (route.personId === "compare") {
      return <Shell me={shellMe}><Compare initial={inlined<CompareData>("snapshot:compare")} /><Toaster /></Shell>;
    }
    if (route.personId) {
      const id = route.personId;
      return (
        <Shell me={shellMe}>
          <ProfilePage id={id} tab={(route.tab ?? "overview") as ProfileTab} canFlag={can(group, me.role ?? null, "flag")} platformAdmin={!!me.platformAdmin}
            onTab={(t) => navigate(`/dashboard/people/${encodeURIComponent(id)}${t === "overview" ? "" : `/${t}`}`, { replace: true })} />
          <Toaster />
        </Shell>
      );
    }
    return <Shell me={shellMe}><PeoplePage role={me.role ?? null} /><Toaster /></Shell>;
  }
  if (route.section === "legislation" || route.section === "forecast-method") {
    return <Shell me={shellMe}><LegislationPage group={shellMe.group} role={me.role ?? null} path={route.path} /><Toaster /></Shell>;
  }
  if (route.section === "organizations") {
    return <Shell me={shellMe}><OrganizationsPage group={shellMe.group} role={me.role ?? null} path={route.path} /><Toaster /></Shell>;
  }
  if (route.section === "meetings") {
    return <Shell me={shellMe}><MeetingsPage canManageTeam={me.role === "owner" || me.role === "admin"} /><Toaster /></Shell>;
  }
  // Chart themes (screenshots/80): /dashboard/overview/themes loads its themes from /api/themes, no snapshot needed.
  if (route.section === "overview" && /^\/dashboard\/overview\/themes\/?$/.test(route.path)) {
    return <Shell me={shellMe}><ThemesPage canTeam={me.role === "owner" || me.role === "admin"} /><Toaster /></Shell>;
  }
  if (route.section === "analytics") {
    // Analytics paints from snapshot:analytics; opened from another page, load it fresh so it does too.
    if (!analyticsStore.getState().snapshot) return loadAsPage();
    const id = route.path.split("/")[3];
    if (!id) return <Shell me={shellMe}><AnalyticsHome /><Toaster /></Shell>;
    return <DashboardRoute key={id} id={decodeURIComponent(id)} me={shellMe} />;
  }
  if (route.section === "overview") {
    // The Overview paints from snapshot:overview; opened from another page (or a dashboard, which reuses its grid), load it fresh.
    if (!overviewStore.getState().snapshot || overviewStore.getState().host !== "overview") return loadAsPage();
    return <Shell me={shellMe}><OverviewPage group={shellMe.group} role={me.role ?? null} /><Toaster /></Shell>;
  }
  // Casework and Campaigns (design section 11): paint from snapshot:casework / snapshot:campaigns / snapshot:campaign.
  if (route.section === "casework") return <Shell me={shellMe}><CaseworkPage /><Toaster /></Shell>;
  if (route.section === "campaigns") return <Shell me={shellMe}><CampaignsPage path={route.path} /><Toaster /></Shell>;
  // Search results (screenshots/114): /dashboard/search?q=
  if (route.section === "search") return <Shell me={shellMe}><SearchPage /><Toaster /></Shell>;
  // Anything else is a 404 (screenshots/107).
  return <System kind="notFound" me={shellMe} />;
}

/** Link expired · 404 · No access (screenshots/106–108). Signed in, "Search ⌘K" opens the palette in place. */
function System({ kind, me }: { kind: SystemKind; me?: Me }) {
  const myPass = me?.signedIn && kind === "noAccess" ? passView({
    name: "", email: me.onboarding?.email ?? "", emailVerified: true, group: me.group ?? null, orgName: me.orgName ?? null, orgStatus: "member",
    ward: me.onboarding?.ward ? { number: me.onboarding.ward, alderpersonName: "" } : null,
    topics: followsStore.getState().follows.filter((f) => f.type === "topic").map((f) => f.label),
    follows: followsStore.getState().follows.filter((f) => f.type === "legislation").map((f) => ({ recordId: f.label })),
  }) : null;
  return <>
    <SystemPage kind={kind} myPass={myPass}
      onRequestAccess={kind === "noAccess" ? () => navigate("/dashboard/analytics") : undefined}
      onSearch={me?.signedIn ? () => dispatchEvent(new CustomEvent("palette:open", { detail: { mode: "search" } })) : undefined} />
    {me?.signedIn && me.group && <CommandPalette />}
  </>;
}

/** A dashboard opened inside Analytics without a page load (after Create, from the home): its grid comes from the API. */
function DashboardRoute({ id, me }: { id: string; me: Me & { group: UserGroup } }) {
  const [state, setState] = useState<"ready" | "loading" | number>(() => (overviewStore.getState().host === `dashboard:${id}` ? "ready" : "loading"));
  useEffect(() => {
    if (state !== "loading") return;
    void fetchDashboard(id).then((r) => {
      if ("error" in r) { setState(r.error); return; }
      openDashboard(r.d, r.grid, analyticsStore.getState().snapshot!.me);
      if (me.orgId) bootOverviewLive(me.orgId);
      setState("ready");
    }).catch(() => setState(500));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (state === 403) return <System kind="noAccess" me={me} />;
  if (state === 404) return <System kind="notFound" me={me} />;
  if (typeof state === "number") return <Shell me={me}><NotAvailable title="Analytics" /><Toaster /></Shell>;
  return <Shell me={me}>{state === "ready" ? <DashboardPage id={id} group={me.group} role={me.role ?? null} /> : null}<Toaster /></Shell>;
}

const me: Me = inlined<Me & { prefs?: NavPrefs }>("snapshot:me") ?? { signedIn: false };
// A meeting's dashboard page with no session: its public page, before anything draws, in place of this history entry
// (app/meetingLinks.ts). The Worker already answers these with a 302 (dashboard.js); this covers a copy of the page
// from the browser's cache, and keeps the #hash.
const publicTwin = me.signedIn ? null : publicMeetingPath(location.pathname, location.search);
if (publicTwin) location.replace(publicTwin + location.hash);
// The public meeting page (/meeting/<id>, dashboard.js publicMeetingPage): anyone, signed in or not.
const publicMeeting = parse(location.pathname).publicMeeting === true;
if (publicMeeting) setViewer({ publicPage: true, admitted: !!me.group, signedIn: me.signedIn });
// Ads: the public meeting page only (its Adsterra slots, ads/AdSlot.tsx, and AdSense Auto ads), where the reader may see
// them (site/js/ad-rules.js: not in the EU/EEA, the UK or Switzerland). The signed-in dashboard has none.
if (publicMeeting) void syncAds(location.pathname); else document.body.classList.add("no-ads");
// Settings → Appearance: the Worker already put the theme in the HTML; keep it current (device dark mode, saves).
initAppearance((me as { appearance?: unknown }).appearance);
// The server's feature map (entitlements with this org's staff overrides, and the role): navConfig `can` answers from it.
initViewerFeatures(me.group ?? null, me.role ?? null, me.features);
// Team positions live (stances on bills, the Alignment list, target lists) arrive on the org channel booted below.
armTeamLive(() => ({ people: peopleStore, list: listStore, detail: legislationDetailStore() }));
navStore.getState().hydrate((me as { prefs?: NavPrefs }).prefs ?? navStore.getState().prefs ?? emptyPrefs());
followsStore.getState().hydrate((me as { follows?: Follow[] }).follows ?? []);
let bootPath = location.pathname;

/** Fill the stores from the page snapshots in the document (inlined by the Worker, or put there by a navigation). */
function hydratePage(pathname: string, search: string) {
  bootPath = pathname;
  const following = inlined<FollowingSnapshot>("snapshot:following");
  if (following) {
    followingStore.getState().hydratePage(following, (me as { settings?: FollowingSettings }).settings ?? null);
    connectionStore.setState({ status: "snapshot", snapshotAt: following.at });
  }
  // People: the directory or a profile paints from its inlined snapshot.
  const people = inlined<PeopleSnapshot>("snapshot:people");
  if (people) {
    peopleStore.getState().hydrate(people);
    connectionStore.setState({ status: "snapshot", snapshotAt: people.at });
  }
  const personPath = parse(pathname).personId;
  if (personPath && personPath !== "compare") {
    const profile = inlined<PersonProfile | { error: number }>(`snapshot:person:${personPath}`);
    if (profile) peopleStore.getState().hydrateProfile(personPath, profile);
  }
  const first = parse(pathname);
  if (me.group && (first.section === "legislation" || first.section === "forecast-method")) bootLegislation(pathname, inlined, search);
  if (me.group && first.section === "organizations") bootOrganizations(pathname, inlined);
  { const ov = inlined<OverviewSnapshot>("snapshot:overview"); if (ov && me.group) { overviewStore.getState().hydrate(ov, me.group); connectionStore.setState({ status: "snapshot", snapshotAt: ov.at }); if (me.orgId) bootOverviewLive(me.orgId); } }
  {
    const an = inlined<AnalyticsSnapshot>("snapshot:analytics");
    if (an && me.group) {
      analyticsStore.getState().hydrate(an);
      const cur = an.current && "grid" in an.current ? an.current : null;
      const d = cur && an.dashboards.find((x) => x.id === cur.id);
      if (cur && d) openDashboard(d, cur.grid, an.me);
      if (me.orgId) bootOverviewLive(me.orgId);
    }
  }
}

// Analytics: the sidebar's starred dashboards need titles on every page (snapshot:me `dashboards`).
analyticsStore.getState().hydrateTitles((me as { dashboards?: DashboardTitle[] }).dashboards ?? []);
{ const n = inlined<NotificationsSnapshot>("snapshot:notifications"); notificationsStore.getState().hydrate(n?.notifications ?? [], !!n?.sample); }
hydratePage(location.pathname, location.search);
const first = parse(location.pathname);

/*
 * In-app navigation without page loads: most pages paint from snapshots the Worker inlines into the HTML it serves
 * for their path (worker/src/dashboard.js dashboardPage). Before a navigation to another page commits, fetch that
 * HTML, swap its page snapshots into this document and hydrate the stores, exactly as a page load would, while the
 * current page stays on screen. Who-you-are (snapshot:me) and notifications keep their live state.
 */
const KEEP = new Set(["snapshot:me", "snapshot:notifications"]);
const CLIENT_ONLY = /^\/dashboard\/(search|overview\/themes)\/?$/;   // these load their own data
let loadedKey = pageKeyOf(location.pathname);
setPreparer(async (url, { force }) => {
  const key = pageKeyOf(url.pathname);
  if (!force && (key === loadedKey || CLIENT_ONLY.test(url.pathname))) return;
  const res = await fetch(url.pathname + url.search, { credentials: "include", headers: { accept: "text/html" } });
  if (!res.ok || !new URL(res.url).pathname.startsWith("/dashboard")) throw new Error(`page ${res.status}`);
  const doc = new DOMParser().parseFromString(await res.text(), "text/html");
  const snaps = [...doc.querySelectorAll<HTMLScriptElement>('script[type="application/json"][id^="snapshot:"]')];
  const nextMe = snaps.find((x) => x.id === "snapshot:me");
  const who = nextMe?.textContent ? (JSON.parse(nextMe.textContent) as Me) : null;
  // A system page (404, expired) or a different session (signed out elsewhere): load it as a page.
  if (!who || who.group !== me.group || who.signedIn !== me.signedIn || snaps.some((x) => x.id === "snapshot:system")) throw new Error("full load");
  document.querySelectorAll('script[type="application/json"][id^="snapshot:"]').forEach((x) => { if (!KEEP.has(x.id)) x.remove(); });
  for (const x of snaps) if (!KEEP.has(x.id)) document.head.appendChild(document.importNode(x, true));
  const target = parse(url.pathname);
  if (target.meetingId && me.group) await boot(target.meetingId, url.search);
  else if (channel) { channel.stop(); channel = null; bootedMeeting = null; }
  hydratePage(url.pathname, url.search);
  loadedKey = key;
});
// A shared dashboard link (/d/<token>): signed out, no sidebar (dashboard-analytics-links.js inlines snapshot:dashlink).
const dashlink = /^\/d\/(pdf\/)?[^/]+\/?$/.test(location.pathname) ? inlined<PublicDashboardSnapshot | { error: number }>("snapshot:dashlink") ?? { error: 404 } : null;
if (dashlink && !("error" in dashlink)) {
  openLink(dashlink.dashboard.link.token, dashlink.grid, dashlink.dashboard.group, dashlink.dashboard.filters);
  if (dashlink.dashboard.link.data === "live") routeDatasetsThroughLink(dashlink.dashboard.link.token);
}
// Every team page listens on the org channel for entitlements.changed (quietly where another channel shows the status).
if (me.group && me.orgId) bootOverviewLive(me.orgId, { quiet: true });
if (publicMeeting) await boot(first.meetingId!, location.search, !!me.group);
else if (me.group && first.meetingId) await boot(first.meetingId);
// The Worker serves system pages with their status and an inlined {kind} (e.g. /d/<token> misses: 410, "expired").
const system = inlined<{ kind: SystemKind }>("snapshot:system");
const root = createRoot(document.getElementById("root")!);
if (system || (dashlink && "error" in dashlink)) root.render(<System kind={system?.kind ?? "expired"} />);
else if (dashlink) root.render(<><PublicDashboard snap={dashlink} /><Toaster /></>);
else if (!publicTwin) {
  interceptLinks();
  root.render(publicMeeting ? <PublicMeeting me={me} /> : <App me={me} />);
  // The meeting video lives in its own root, beside the app, so no page change or remount ever reloads it
  // (drawn over the meeting page's slot, or docked while you browse; components/meeting/VideoStage.tsx).
  if (me.group || publicMeeting) {
    const host = Object.assign(document.createElement("div"), { id: "player-root" });
    document.body.appendChild(host);
    createRoot(host).render(<PlayerHost />);
  }
}
addEventListener("pagehide", () => channel?.stop());
